import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  DocumentIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  DocumentTextIcon,
  PhotoIcon
} from '@heroicons/react/24/solid';
import axios from 'axios';
import { useConfirm } from './ConfirmDialog';

const FileList = ({ classroomId, userId, userRole, refreshTrigger }) => {
  const confirm = useConfirm();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFiles();
  }, [classroomId, refreshTrigger]);

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`/api/files/${classroomId}`);
      setFiles(response.data);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await axios.get(
        `/api/download/${file.classroom_id}/${file.filename}`,
        { responseType: 'blob' }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('File downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDelete = async (fileId) => {
    if (!(await confirm({
      title: 'Delete file?',
      message: 'This permanently removes the file. This can\'t be undone.',
      confirmLabel: 'Delete',
    }))) {
      return;
    }

    try {
      await axios.delete(`/api/files/${fileId}`);
      setFiles(files.filter(f => f.id !== fileId));
      toast.success('File deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete file');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) {
      return <PhotoIcon className="h-10 w-10 text-blue-600" />;
    } else if (mimeType === 'application/pdf') {
      return <DocumentTextIcon className="h-10 w-10 text-red-600" />;
    } else {
      return <DocumentIcon className="h-10 w-10 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Loading files...</p>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="text-center py-8">
        <DocumentIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-500">No files uploaded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {files.map((file) => (
        <div
          key={file.id}
          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              {getFileIcon(file.mime_type)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {file.original_filename}
                </p>
                <p className="text-xs text-gray-500">
                  Uploaded by {file.uploader?.name || 'Unknown'} • {formatFileSize(file.file_size)}
                </p>
                {file.description && (
                  <p className="text-xs text-gray-600 mt-1">{file.description}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  {new Date(file.created_at).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-2">
              <button
                onClick={() => handleDownload(file)}
                className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title="Download"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
              {(file.uploader_id === userId || userRole === 'tutor' || userRole === 'admin') && (
                <button
                  onClick={() => handleDelete(file.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FileList;
